import { createFileRoute } from "@tanstack/react-router";
import type { ReactNode } from "react";
import { PageShell } from "@/components/page-shell";
import { ObfuscatedEmail } from "@/components/obfuscated-email";

export const Route = createFileRoute("/express")({
  head: () => ({
    meta: [
      { title: "Express — Brand Strategy Services, Fast | WeVang!" },
      {
        name: "description",
        content:
          "A focused brand strategy system — Signal, Sprint, and Retainer — for a single logo, audit, landing page, or channel.",
      },
      { property: "og:title", content: "Express — Brand Strategy Services, Fast | WeVang!" },
      {
        property: "og:description",
        content:
          "A focused brand strategy system — Signal, Sprint, and Retainer — for a single logo, audit, landing page, or channel.",
      },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary_large_image" },
      { property: "og:url", content: "https://www.wevang.com/express" },
    ],
    links: [{ rel: "canonical", href: "https://www.wevang.com/express" }],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "Service",
          name: "WeVang! Express",
          serviceType: "Brand strategy consulting",
          description: "Fixed-scope brand and strategy services delivered as signals, sprints and retainers.",
          url: "https://www.wevang.com/express",
          provider: {
            "@type": "Organization",
            name: "WeVang!",
            url: "https://www.wevang.com",
          },
        }),
      },
    ],
  }),
  component: ExpressPage,
});

function Eyebrow({ children }: { children: ReactNode }) {
  return (
    <div className="flex items-start gap-4">
      <span className="mt-[0.55rem] h-px w-10 shrink-0 bg-primary" />
      <p className="font-bold uppercase leading-relaxed tracking-[0.28em] text-[14px] text-primary">
        {children}
      </p>
    </div>
  );
}

function Section({
  children,
  watermark,
  className = "",
}: {
  children: ReactNode;
  watermark?: string;
  className?: string;
}) {
  return (
    <section
      className={`relative isolate overflow-hidden px-6 py-24 md:px-12 md:py-36 ${className}`}
    >
      {watermark && (
        <span
          aria-hidden
          className="watermark pointer-events-none absolute bottom-4 right-2 -z-10 hidden select-none text-[6.5vw] opacity-[0.07] lg:block"
        >
          {watermark}
        </span>
      )}
      <div className="relative mx-auto max-w-[1400px]">
        <div className="max-w-4xl">{children}</div>
      </div>
    </section>
  );
}

function FormatCard({
  number,
  heading,
  body,
}: {
  number: string;
  heading: string;
  body: string;
}) {
  return (
    <article className="group border border-border bg-white p-10 transition-colors duration-250 ease-out hover:bg-[#1A1A1A] hover:text-white">
      <div className="font-display text-[48px] font-extrabold leading-none text-primary">
        {number}
      </div>
      <h3 className="mt-6 font-display text-[32px] font-extrabold tracking-tighter text-foreground transition-colors duration-250 ease-out group-hover:text-primary">
        {heading}
      </h3>
      <p className="mt-4 font-sans text-[18px] font-normal leading-relaxed text-foreground/60 transition-colors duration-250 ease-out group-hover:text-white/70">
        {body}
      </p>
    </article>
  );
}

type Service = { name: string; tag: string; message: string; price: string };

const FAMILIES: { family: string; services: Service[] }[] = [
  {
    family: "Brand and Identity",
    services: [
      { name: "Identity Signal", tag: "Signal", message: "Pick one area — logo, copy, or social. Get 3 fixes in 3–5 days.", price: "Starting at $700 USD / €" },
      { name: "Identity Sprint", tag: "Sprint", message: "One real logo concept, palette, and typefaces. Not three options to pick from.", price: "Starting at $1,500 USD / €" },
    ],
  },
  {
    family: "Digital Presence",
    services: [
      { name: "Site Signal", tag: "Signal", message: "A 3-page audit of your existing site: UX, speed, SEO, AI visibility.", price: "Starting at $700 USD / €" },
      { name: "Site Sprint", tag: "Sprint", message: "A 3–6 page site, built on the structure you bring.", price: "Starting at $2,300 USD / €" },
      { name: "Landing Sprint", tag: "Sprint", message: "One live landing page, max 4 sections, copy included.", price: "Starting at $1,400 USD / €" },
      { name: "SEO Sprint", tag: "Sprint", message: "Technical fixes plus a content plan built on real keyword gaps.", price: "Starting at $2,000 USD / €" },
    ],
  },
  {
    family: "Growth and Marketing",
    services: [
      { name: "Growth Signal", tag: "Signal", message: "Where your traffic comes from, what converts — boiled down to 3 priorities.", price: "Starting at $700 USD / €" },
      { name: "Growth Sprint", tag: "Sprint", message: "A paid campaign, planned, launched, and reported — one channel.", price: "Starting at $2,000 USD / €" },
      { name: "Growth Retainer", tag: "Retainer", message: "Monthly optimization on a campaign already live.", price: "Starting at $900/mo USD / €" },
      { name: "Social Media Sprint", tag: "Sprint", message: "A content calendar for one platform — you publish.", price: "Starting at $700 USD / €" },
      { name: "Social Media Retainer", tag: "Retainer", message: "We create and publish, every month, ongoing.", price: "Starting at $500/mo USD / €" },
      { name: "Email Marketing Sprint", tag: "Sprint", message: "A 3–5 email sequence, built on the platform you already use.", price: "Starting at $1,400 USD / €" },
      { name: "Email Marketing Retainer", tag: "Retainer", message: "Ongoing sequences, newsletters, and monthly optimization.", price: "Starting at $900/mo USD / €" },
    ],
  },
  {
    family: "Strategy",
    services: [
      { name: "Sprint Unlock", tag: "Sprint", message: "One senior call to unblock one decision — no full research.", price: "Starting at $900 USD / €" },
    ],
  },
];

function ServiceCard({ service }: { service: Service }) {
  return (
    <article className="group flex flex-col border border-border bg-white p-7 transition-transform duration-200 ease-out hover:-translate-y-0.5 hover:border-primary hover:shadow-lg">
      <span className="self-start border border-primary/40 bg-primary/8 px-2.5 py-1 font-sans text-[12px] font-bold uppercase tracking-[0.18em] text-primary">
        {service.tag}
      </span>
      <h4 className="mt-5 font-display text-[24px] font-extrabold leading-tight tracking-tighter text-foreground">
        {service.name}
      </h4>
      <p className="mt-3 font-sans text-[16px] font-normal leading-relaxed text-foreground/60">
        {service.message}
      </p>
      <p className="mt-6 font-sans text-[20px] font-bold text-foreground">{service.price}</p>
    </article>
  );
}


function ExpressPage() {
  return (
    <PageShell>
      {/* HERO */}
      <section className="relative isolate overflow-hidden bg-white px-6 pb-28 pt-48 md:px-12 md:pb-40 md:pt-56">
        <span
          aria-hidden
          className="watermark pointer-events-none absolute -right-4 top-24 -z-10 hidden select-none text-[16vw] leading-none opacity-[0.07] lg:block"
        >
          Express
        </span>
        <div className="relative mx-auto max-w-[1400px]">
          <Eyebrow>Solved Fast, Solved Well</Eyebrow>
          <h1 className="mt-10 max-w-5xl font-display text-[48px] font-extrabold leading-[1.02] tracking-tighter text-foreground md:text-[72px]">
            You don&apos;t need a full senior team. You need it{" "}
            <span className="font-extrabold text-primary">done right</span>.
          </h1>
          <p className="mt-10 max-w-4xl font-sans text-[24px] font-light leading-[1.6] text-foreground/60">
            Express delivers single, focused pieces — a logo, an audit, a landing page, a channel —
            with the same standard as our biggest engagements. No watered-down version. Just the
            right scope for what you actually need right now.
          </p>
        </div>
      </section>

      {/* WHAT EXPRESS IS */}
      <Section watermark="Piece">
        <Eyebrow>This Isn&apos;t a Smaller Journey</Eyebrow>
        <h2 className="mt-10 font-display text-[40px] font-extrabold leading-[1.05] tracking-tighter text-foreground md:text-[56px]">
          Journeys rebuilds the business. Express{" "}
          <span className="font-extrabold text-primary">fixes the piece</span>.
        </h2>
        <p className="mt-8 font-sans text-[20px] font-normal leading-[1.7] text-foreground/60">
          A logo. A site audit. A landing page. One channel. Express solves one isolated problem,
          fast — it doesn&apos;t integrate a business decision the way Journeys does. Same standard,
          different scope.
        </p>
      </Section>

      {/* HOW IT WORKS */}
      <section className="relative isolate overflow-hidden px-6 py-24 md:px-12 md:py-36">
        <div className="mx-auto max-w-[1400px]">
          <Eyebrow>Three Formats, Every Service</Eyebrow>
          <div className="mt-16 grid grid-cols-1 gap-6 md:grid-cols-3">
            <FormatCard
              number="01"
              heading="Signal"
              body="A closed-scope audit. What’s failing, why, and 3 concrete fixes — in 3 to 5 days. Honest diagnosis, not a redesign."
            />
            <FormatCard
              number="02"
              heading="Sprint"
              body="Real production, not a sketch. We build the asset — logo, site, landing page, sequence — in 1 to 2 weeks."
            />
            <FormatCard
              number="03"
              heading="Retainer"
              body="Ongoing monthly production for the channels that need continuity — growth, social, email. No reinventing the plan every month."
            />
          </div>
        </div>
      </section>

      {/* SERVICES BY FAMILY */}
      <section className="relative isolate overflow-hidden px-6 py-24 md:px-12 md:py-36">
        <span
          aria-hidden
          className="watermark pointer-events-none absolute bottom-4 right-2 -z-10 hidden select-none text-[6.5vw] opacity-[0.07] lg:block"
        >
          Services
        </span>
        <div className="relative mx-auto max-w-[1400px]">
          <Eyebrow>Four Families, Fourteen Services</Eyebrow>
          <h2 className="mt-10 max-w-4xl font-display text-[32px] font-bold leading-[1.1] tracking-tighter text-foreground md:text-[44px]">
            Pick the piece. We bring the standard.
          </h2>

          <div className="mt-20 space-y-20">
            {FAMILIES.map((f) => (
              <div key={f.family}>
                <h3 className="font-display text-[32px] font-extrabold tracking-tighter text-foreground">
                  {f.family}
                </h3>
                <div className="mt-8 grid grid-cols-1 gap-6 border-t border-border pt-8 sm:grid-cols-2 lg:grid-cols-3">
                  {f.services.map((s) => (
                    <ServiceCard key={s.name} service={s} />
                  ))}
                </div>

              </div>
            ))}
          </div>
        </div>
      </section>

      {/* WHY EXPRESS */}
      <Section watermark="Standard">
        <Eyebrow>Why Not Just Hire It Out for $50?</Eyebrow>
        <h2 className="mt-10 font-display text-[36px] font-bold leading-[1.05] tracking-tighter text-foreground md:text-[48px]">
          Cheap isn&apos;t the risk. <span className="font-bold text-primary">Inconsistent is.</span>
        </h2>
        <p className="mt-8 font-sans text-[20px] font-normal leading-[1.7] text-foreground/60">
          A freelancer who disappears halfway through doesn&apos;t just cost you money — it costs
          you the time to find, brief, and fix what they left behind. Every Express deliverable gets
          peer review from our senior team before it reaches you. Same standard as a $45K
          engagement, sized for what you need today.
        </p>
      </Section>

      {/* THE OUTCOME */}
      <div className="surface-light">
        <Section watermark="Outcome">
          <Eyebrow>The Outcome</Eyebrow>
          <h2 className="mt-10 font-display text-[32px] font-bold leading-[1.1] tracking-tighter text-foreground md:text-[44px]">
            Fast doesn&apos;t mean disposable.
          </h2>
          <p className="mt-8 font-sans text-[22px] font-normal leading-[1.6] text-foreground/60">
            Every Express deliverable meets the same bar as our largest work — because the standard
            isn&apos;t the ticket size,{" "}
            <span className="font-bold text-primary">it&apos;s the standard.</span>
          </p>
        </Section>
      </div>

      {/* CTA */}
      <section className="px-6 pb-40 pt-24 text-center md:px-12 md:pb-52">
        <ObfuscatedEmail className="text-sm tracking-[0.3em] text-foreground/70 transition-colors hover:text-primary" />
      </section>
    </PageShell>
  );
}
